import React, { useEffect, useState, useMemo } from "react";
import { Translator } from "./translator";

const Legal = () => {
  const { translateText, lang } = Translator();

  // ===== Fixed IT / EN / FR text =====
  const baseTextByLang = useMemo(
    () => ({
      it: {
        title: "Informazioni Legali",
        intro:
          "Benvenuto nella sezione Informazioni Legali di Te.C.H-e. Qui forniamo tutti i dettagli relativi ai termini di utilizzo, alla responsabilità legale e alle informazioni societarie.",

        section1: "Dati Societari",
        section1Content:
          "Denominazione: Te.C.H-e Technology for Cultural Heritage\n" +
          "Sede legale: Via Giuseppe Verdi 46, 80038 Pomigliano d'Arco, Italia\n" +
          "C.F. e P.IVA: 09885961210\n" +
          "Email di contatto: emozionartisrl@gmail.com",

        section2: "Termini di Utilizzo",
        section2Content:
          "L'accesso e l'utilizzo della piattaforma Te.C.H-e e dell'app TecheTour sono soggetti ai termini e condizioni stabiliti. Utilizzando i nostri servizi, l'utente accetta integralmente tali termini.",

        section3: "Diritti di Proprietà Intellettuale",
        section3Content:
          "Tutti i contenuti, i marchi, i loghi, i testi, le immagini, i video e i materiali presenti su questa piattaforma sono di proprietà di Te.C.H-e o dei rispettivi titolari, protetti dalle leggi italiane ed europee sul diritto d'autore e sulla proprietà intellettuale.",

        section4: "Limitazione di Responsabilità",
        section4Content:
          "Te.C.H-e non potrà essere ritenuta responsabile per eventuali danni diretti o indiretti derivanti dall'uso delle informazioni contenute nel sito o dall'impossibilità di accesso ai servizi.",

        section5: "Modifiche alle Informazioni Legali",
        section5Content:
          "Te.C.H-e si riserva il diritto di modificare le presenti Informazioni Legali in qualsiasi momento. Eventuali modifiche saranno comunicate tramite aggiornamenti su questa pagina.",

        footer: "© {year} Te.C.H-e – Tutti i diritti riservati.",
      },

      en: {
        title: "Legal Information",
        intro:
          "Welcome to the Legal Information section of Te.C.H-e. Here we provide all details regarding the terms of use, legal liability, and company information.",

        section1: "Company Details",
        section1Content:
          "Company name: Te.C.H-e Technology for Cultural Heritage\n" +
          "Registered office: Via Giuseppe Verdi 46, 80038 Pomigliano d'Arco, Italy\n" +
          "Tax ID and VAT No.: 09885961210\n" +
          "Contact email: emozionartisrl@gmail.com",

        section2: "Terms of Use",
        section2Content:
          "Access to and use of the Te.C.H-e platform and the TecheTour app are subject to the applicable terms and conditions. By using our services, you fully accept these terms.",

        section3: "Intellectual Property Rights",
        section3Content:
          "All content, trademarks, logos, text, images, videos and materials on this platform are the property of Te.C.H-e or their respective owners and are protected by Italian and European copyright and intellectual property laws.",

        section4: "Limitation of Liability",
        section4Content:
          "Te.C.H-e cannot be held liable for any direct or indirect damages arising from the use of the information contained on the site or from the inability to access the services.",

        section5: "Changes to Legal Information",
        section5Content:
          "Te.C.H-e reserves the right to modify this Legal Information at any time. Any changes will be communicated by updating this page.",

        footer: "© {year} Te.C.H-e – All rights reserved.",
      },

      fr: {
        title: "Informations légales",
        intro:
          "Bienvenue dans la section Informations légales de Te.C.H-e. Vous trouverez ici tous les détails relatifs aux conditions d’utilisation, à la responsabilité juridique et aux informations sur la société.",

        section1: "Informations sur la société",
        section1Content:
          "Dénomination sociale : Te.C.H-e Technology for Cultural Heritage\n" +
          "Siège social : Via Giuseppe Verdi 46, 80038 Pomigliano d'Arco, Italie\n" +
          "Code fiscal et N° de TVA : 09885961210\n" +
          "Adresse e-mail de contact : emozionartisrl@gmail.com",

        section2: "Conditions d’utilisation",
        section2Content:
          "L’accès et l’utilisation de la plateforme Te.C.H-e et de l’application TecheTour sont soumis aux conditions générales en vigueur. En utilisant nos services, l’utilisateur accepte pleinement ces conditions.",

        section3: "Droits de propriété intellectuelle",
        section3Content:
          "Tous les contenus, marques, logos, textes, images, vidéos et matériaux présents sur cette plateforme sont la propriété de Te.C.H-e ou de leurs titulaires respectifs et sont protégés par les lois italiennes et européennes sur le droit d’auteur et la propriété intellectuelle.",

        section4: "Limitation de responsabilité",
        section4Content:
          "Te.C.H-e ne pourra pas être tenue responsable des dommages directs ou indirects résultant de l’utilisation des informations contenues sur le site ou de l’impossibilité d’accéder aux services.",

        section5: "Modifications des informations légales",
        section5Content:
          "Te.C.H-e se réserve le droit de modifier les présentes Informations légales à tout moment. Toute modification sera communiquée par une mise à jour de cette page.",

        footer: "© {year} Te.C.H-e – Tous droits réservés.",
      },
    }),
    []
  );

  const [text, setText] = useState(
    baseTextByLang[lang] || baseTextByLang.en
  );

  // ===== IT / EN / FR fixed; other languages via Translator =====
  useEffect(() => {
    // For Italian, English, French → use fixed copy
    if (lang === "it" || lang === "en" || lang === "fr") {
      setText(baseTextByLang[lang] || baseTextByLang.en);
      return;
    }

    // For other languages → translate from Italian base
    let cancelled = false;

    const translateAll = async () => {
      try {
        const source = baseTextByLang.it;
        const entries = Object.entries(source);
        const translatedEntries = await Promise.all(
          entries.map(async ([key, value]) => {
            const out = await translateText(value);
            return [key, out || value];
          })
        );
        if (!cancelled) {
          setText(Object.fromEntries(translatedEntries));
        }
      } catch {
        if (!cancelled) {
          setText(baseTextByLang.en);
        }
      }
    };

    translateAll();
    return () => {
      cancelled = true;
    };
  }, [lang, translateText, baseTextByLang]);

  return (
    <div className="max-w-4xl mx-auto px-4 py-10 font-sans text-gray-800">
      <h1 className="text-black text-3xl md:text-4xl font-bold text-center mb-6">
        {text.title}
      </h1>
      <p className="mb-6">{text.intro}</p>

      <Section title={text.section1}>
        {(text.section1Content || "")
          .split("\n")
          .map((line, idx) => (
            <p key={idx} className="mb-1">
              {line}
            </p>
          ))}
      </Section>

      <Section title={text.section2}>
        <p>{text.section2Content}</p>
      </Section>

      <Section title={text.section3}>
        <p>{text.section3Content}</p>
      </Section>

      <Section title={text.section4}>
        <p>{text.section4Content}</p>
      </Section>

      <Section title={text.section5}>
        <p>{text.section5Content}</p>
      </Section>

      <footer className="text-sm text-center text-gray-500 mt-10">
        {text.footer.replace("{year}", new Date().getFullYear())}
      </footer>
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="text-black text-2xl font-semibold mb-3">
      {title}
    </h2>
    <div className="text-base">{children}</div>
  </section>
);

export default Legal;
